import React, { useCallback, useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import QuickSearchToolbar from '~/components/Common/Elements/QuickSearchToolbar';

import { useGetAllBundles } from '~/hooks/apis/accommodation/useBundles';
import useAppNavigation from '~/hooks/useAppNavigation';

import { Bundle, BundleHotelRule, BundleOffer } from '~/services/AccommodationService';

import { BOARD_CODE_LABELS, BUNDLE_CATEGORY_LABELS, bundleStatusPillProps } from './constants';

const columns: Array<GridColDef> = [
  {
    field: 'name',
    headerName: 'Bundle Name',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'id',
    headerName: 'Bundle ID',
    flex: 1,
    minWidth: 280,
  },
  {
    field: 'tagId',
    headerName: 'Tag ID',
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: ({ value }) => {
      const { label, color } = bundleStatusPillProps(value as Bundle['status']);
      return <Chip label={label} color={color} size="small" variant="outlined" />;
    },
  },
  {
    field: 'category',
    headerName: 'Category',
    width: 160,
    valueGetter: (value: Bundle['category']) => (value ? BUNDLE_CATEGORY_LABELS[value] : 'Uncategorised'),
  },
  {
    field: 'offers',
    headerName: 'Offers',
    width: 100,
    valueGetter: (_value, row) => row.offers?.length ?? 0,
  },
  {
    field: 'rules',
    headerName: 'Rules',
    width: 100,
    valueGetter: (_value, row) => row.rules?.length ?? 0,
  },
  {
    field: 'schedules',
    headerName: 'Schedules',
    width: 100,
    valueGetter: (_value, row) => row.schedules?.length ?? 0,
  },
  {
    field: 'boardCodes',
    headerName: 'Board Codes',
    flex: 1,
    minWidth: 180,
    sortable: false,
    valueGetter: (_value, row) => {
      const allCodes = (row.rules ?? []).flatMap((r: BundleHotelRule) => r.boardCodes);
      return [...new Set(allCodes)];
    },
    renderCell: ({ value }) => (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ py: 0.5 }}>
        {(value as Array<string>).map((code) => (
          <Chip key={code} label={BOARD_CODE_LABELS[code] ?? code} size="small" variant="outlined" />
        ))}
      </Stack>
    ),
  },
  {
    field: 'searchableIds',
    headerName: 'Associated IDs',
    valueGetter: (_value, row) => {
      // Flatten every id/name a user might paste into the quick filter. entityId covers the
      // experience / deal option id (e.g. `led:006...`); ticket productIds (e.g. `a0sRF...`)
      // and fareTypes come from the experience offers' tickets and are otherwise unsearchable.
      const offerValues = (row.offers ?? []).flatMap((o: BundleOffer) => [
        o.entityId,
        o.propertyId,
        ...(o.originAirports ?? []),
        o.experienceName,
        o.experienceDescription,
        ...(o.tickets ?? []).flatMap((t) => [t.productId, t.name, t.fareType]),
      ]);
      return offerValues.filter(Boolean).join(' ');
    },
  },
];

export default function AccommodationBundlesContainer() {
  const { push } = useAppNavigation();
  const { data, isLoading, error } = useGetAllBundles();
  const { search } = useLocation();

  // Seed the quick filter from a `?q=` param so a bundle/deal-option/product id can be
  // deep-linked straight into a filtered list. Trimmed and split on whitespace to match the
  // quick filter's own tokenising; empty values fall back to an unfiltered grid.
  const initialQuickFilterValues = useMemo(() => {
    const q = new URLSearchParams(search).get('q')?.trim();
    return q ? q.split(/\s+/) : [];
  }, [search]);

  const handleRowClick = useCallback(
    (params: { row: Bundle }) => {
      push(`/accommodation/bundles/${params.row.id}`);
    },
    [push],
  );

  const handleCreate = useCallback(() => {
    push('/accommodation/bundles/new');
  }, [push]);

  if (error) {
    return (
      <Alert severity="error">
        <Typography>Failed to load bundles: {error.message}</Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" onClick={handleCreate}>
          + New Bundle
        </Button>
      </Box>

      {isLoading && (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress variant="indeterminate" />
        </Box>
      )}

      {!isLoading && (
        <DataGrid
          rows={data?.bundles ?? []}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            columns: {
              columnVisibilityModel: {
                searchableIds: false,
              },
            },
            filter: {
              filterModel: {
                items: [],
                quickFilterExcludeHiddenColumns: false,
                quickFilterValues: initialQuickFilterValues,
              },
            },
          }}
          onRowClick={handleRowClick}
          autoHeight
          disableRowSelectionOnClick
          slots={{ toolbar: QuickSearchToolbar }}
          sx={{ cursor: 'pointer' }}
        />
      )}
    </Box>
  );
}
